import React, { createContext, useContext, useCallback } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';

const RecordRouteApiContext = createContext(null);

const informationPackageGoogleDriveMimeTypes = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/html',
  'text/html; charset=utf-8',
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
];

export function RecordRouteApiProvider({ children }) {
  const { get, post, put, del } = useApiService(CONFIG.SERVER_ORGANIZATION);
  const { get: get_channels } = useApiService(CONFIG.SERVER_CHANNEL);
  const { get: get_agents } = useApiService(CONFIG.SERVER_ASSISTANT);
  const { get: get_documents } = useApiService(CONFIG.SERVER_DOCUMENT);

  const getRecordRoute = useCallback(
    async (skip, limit) => {
      if (!skip) {
        skip = 0;
      }
      if (!limit) {
        limit = 10;
      }
      try {
        const response = await get(`/v1/record_route?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching record route:', error);
        throw error;
      }
    },
    [get],
  );

  const getRecordRouteById = useCallback(
    async (recordRouteId) => {
      try {
        const response = await get(`/v1/record_route/${recordRouteId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching record route by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createRecordRoute = useCallback(
    async (recordRoute) => {
      try {
        const response = await post(`/v1/record_route`, recordRoute);
        return response.data;
      } catch (error) {
        console.error('Error creating record route:', error);
        throw error;
      }
    },
    [post],
  );

  const updateRecordRouteById = useCallback(
    async (recordRouteId, recordRoute) => {
      try {
        const response = await put(`/v1/record_route/${recordRouteId}`, recordRoute);
        return response.data;
      } catch (error) {
        console.error('Error updating record route:', error);
        throw error;
      }
    },
    [put],
  );

  const triggerEventById = useCallback(
    async (recordRouteId) => {
      try {
        const response = await post(`/v1/record_route/${recordRouteId}/trigger`);
        return response.data;
      } catch (error) {
        console.error('Error trigger record route:', error);
        throw error;
      }
    },
    [post],
  );

  const deleteRecordRoute = useCallback(
    async (recordRouteId) => {
      try {
        const response = await del(`/v1/record_route/${recordRouteId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting record route:', error);
        throw error;
      }
    },
    [del],
  );

  const getChannels = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get_channels(`/v1/channels?skip=${skip}&limit=${limit}`);
        return response.data.items ?? [];
      } catch (error) {
        console.error('Error fetching channels:', error);
        throw error;
      }
    },
    [get_channels],
  );

  const getAgents = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get_agents(`/v1/assistants?skip=${skip}&limit=${limit}`);
        return response.data.items ?? [];
      } catch (error) {
        console.error('Error fetching agents:', error);
        throw error;
      }
    },
    [get_agents],
  );

  const getDocumentCollections = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get_documents(`/v1/document_collection?skip=${skip}&limit=${limit}`);
        return response.data.items ?? [];
      } catch (error) {
        console.error('Error fetching document collections:', error);
        throw error;
      }
    },
    [get_documents],
  );

  const getPackageCollections = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get_documents(
          `/v1/information_package_collection?skip=${skip}&limit=${limit}`,
        );
        return response.data.items ?? [];
      } catch (error) {
        console.error('Error fetching information package collections:', error);
        throw error;
      }
    },
    [get_documents],
  );

  const getDocuments = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get_documents(`/v1/documents?skip=${skip}&limit=${limit}`);
        return response.data.items ?? [];
      } catch (error) {
        console.error('Error fetching document:', error);
        throw error;
      }
    },
    [get_documents],
  );

  const getZendeskConnections = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get(`/v1/connections/zendesk?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching connection:', error);
        throw error;
      }
    },
    [get],
  );

  const getCreatioConnections = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get(`/v1/connections/creatio?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching connection:', error);
        throw error;
      }
    },
    [get],
  );

  const getNotionConnections = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get(`/v1/connections/notion?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching connection:', error);
        throw error;
      }
    },
    [get],
  );

  const getGoogleConnections = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get(`/v1/connections/google?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching connection:', error);
        throw error;
      }
    },
    [get],
  );

  const getGoogleDriveFiles = useCallback(
    async (connectionKey, params = {}) => {
      try {
        const { limit = 100, pageToken = null, searchQuery = null } = params;
        const searchParameters = new URLSearchParams();
        searchParameters.set('connection_object_type', 'FILE');
        searchParameters.set('connection_key', connectionKey);
        searchParameters.set('limit', String(limit));

        if (pageToken) {
          searchParameters.set('page_token', String(pageToken));
        }
        if (searchQuery) {
          searchParameters.set('search_query', String(searchQuery));
        }

        const response = await get(`/v1/connection_objects/files?${searchParameters.toString()}`);
        return {
          ...response.data,
          items: (response.data.items ?? []).filter((googleDriveFile) =>
            informationPackageGoogleDriveMimeTypes.includes(googleDriveFile.mime_type),
          ),
        };
      } catch (error) {
        console.error('Error fetching Google Drive files:', error);
        throw error;
      }
    },
    [get],
  );

  const getIntercomConnections = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get(`/v1/connections/intercom?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching connection:', error);
        throw error;
      }
    },
    [get],
  );

  const getConnectionObjectOptions = useCallback(
    async (skip = 0, limit = 50, name = '') => {
      try {
        const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
        if (name) params.set('name', name);
        const response = await get(`/v1/connection_objects/options?${params.toString()}`);
        return {
          items: response.data.items ?? [],
          totalCount: response.data.totalCount ?? 0,
        };
      } catch (error) {
        console.error('Error fetching connection object options:', error);
        throw error;
      }
    },
    [get],
  );

  const getConnectionObjectById = useCallback(
    async (connectionObjectId) => {
      try {
        const response = await get(`/v1/connection_objects/${connectionObjectId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching connection object by ID:', error);
        throw error;
      }
    },
    [get],
  );

  return (
    <RecordRouteApiContext.Provider
      value={{
        getRecordRoute,
        getRecordRouteById,
        createRecordRoute,
        updateRecordRouteById,
        deleteRecordRoute,
        getChannels,
        getAgents,
        getDocuments,
        getDocumentCollections,
        getPackageCollections,
        getCreatioConnections,
        getZendeskConnections,
        getNotionConnections,
        triggerEventById,
        getGoogleConnections,
        getGoogleDriveFiles,
        getIntercomConnections,
        getConnectionObjectOptions,
        getConnectionObjectById,
      }}
    >
      {children}
    </RecordRouteApiContext.Provider>
  );
}

export function useRecordRouteApi() {
  const context = useContext(RecordRouteApiContext);
  if (!context) {
    throw new Error('useRecordRouteApi must be used within a RecordRouteApiProvider');
  }
  return context;
}
